import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { NotificationPreferenceUpdate } from './types';

export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (p: api.NotificationListParams) => [...notificationKeys.lists(), p] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
};

/** How often the bell re-reads its lists. The poll is the fallback that keeps
 *  the badge right when no live update arrives (DEV13-65); a hidden tab
 *  doesn't poll, and focusing it refetches once the data is stale (the app's
 *  30 s staleTime). While the bell's realtime channel
 *  is live the poll only relaxes — it never stops, since a socket may drop
 *  without the page noticing at once (FE-ADR-066). */
export const BELL_POLL_MS = 60_000;
export const BELL_POLL_LIVE_MS = 5 * 60_000;

export function useNotifications(
  params: api.NotificationListParams = {},
  { poll = false, live = false }: { poll?: boolean; live?: boolean } = {},
) {
  const interval = live ? BELL_POLL_LIVE_MS : BELL_POLL_MS;
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => api.listNotifications(params),
    placeholderData: keepPreviousData,
    refetchInterval: poll ? interval : false,
    refetchOnWindowFocus: poll,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.markNotificationRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.lists() }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: ({ marked }) => {
      qc.invalidateQueries({ queryKey: notificationKeys.lists() });
      toast.success(marked ? `Marked ${marked} as read` : 'Nothing to mark');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

/** Wave 11 — per-user channel preferences. */
export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: () => api.getNotificationPreferences(),
  });
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NotificationPreferenceUpdate) =>
      api.updateNotificationPreferences(input),
    onSuccess: (data) => {
      qc.setQueryData(notificationKeys.preferences(), data);
      // Confirmed in place by the page's SaveIndicator (FE-ADR-065).
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
